import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Loading from '../components/Loading';

const ExpenseDetail = () => {
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchExpense();
  }, [id]);

  const fetchExpense = async () => {
    try {
      const res = await axios.get(`/api/expenses/${id}`);
      setExpense(res.data);
    } catch (error) {
      console.error('Error fetching expense:', error);
      navigate('/expenses');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFileUrl = (path) => {
    const filename = path.split('/').pop();
    return `/uploads/${filename}`;
  };

  if (loading) return <Loading />;
  if (!expense) return null;

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <button onClick={() => navigate(-1)} className="btn btn-outline btn-sm" style={{ marginBottom: '12px' }}>
            ← Back
          </button>
          <h1 className="page-title">{expense.title}</h1>
          <p className="page-subtitle">Expense Report Details</p>
        </div>
        <span className={`badge badge-${expense.status}`} style={{ fontSize: '0.875rem', padding: '8px 16px' }}>
          {expense.status}
        </span>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h2 className="card-title">Expense Information</h2>
        </div>
        <div className="card-body">
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Amount</span>
              <span className="detail-value" style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>
                {formatCurrency(expense.amount)}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Category</span>
              <span className="detail-value">{expense.category}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Expense Date</span>
              <span className="detail-value">{formatDate(expense.expenseDate)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Submitted On</span>
              <span className="detail-value">{formatDateTime(expense.createdAt)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Payment Type</span>
              <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                {expense.paymentType || 'Reimbursement'}
              </span>
            </div>
            {expense.paidAt && (
              <div className="detail-item">
                <span className="detail-label">Paid On</span>
                <span className="detail-value">{formatDateTime(expense.paidAt)}</span>
              </div>
            )}
          </div>

          <div style={{ marginTop: '24px' }}>
            <span className="detail-label">Description</span>
            <p style={{ marginTop: '8px', color: 'var(--gray-700)' }}>{expense.description}</p>
          </div>

          {expense.adminComment && (
            <div style={{ 
              marginTop: '24px', 
              padding: '16px', 
              background: 'var(--gray-50)', 
              borderRadius: 'var(--radius)',
              borderLeft: '4px solid var(--primary)',
            }}>
              <span className="detail-label">Admin Comment</span>
              <p style={{ marginTop: '8px', color: 'var(--gray-700)' }}>{expense.adminComment}</p>
            </div>
          )}
        </div>
      </div>

      {expense.attachments && expense.attachments.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Attachments ({expense.attachments.length})</h2>
          </div>
          <div className="card-body">
            <div className="attachments-grid">
              {expense.attachments.map((file, index) => (
                <a
                  key={index}
                  href={getFileUrl(file.path)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="attachment-card"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="attachment-preview">
                    {file.mimetype === 'application/pdf' ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <text x="7" y="17" fontSize="6" fill="currentColor" stroke="none">PDF</text>
                      </svg>
                    ) : (
                      <img src={getFileUrl(file.path)} alt={file.originalName} />
                    )}
                  </div>
                  <div className="attachment-info">
                    <div className="attachment-name">{file.originalName}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseDetail;
